import { Button, Col, Row, notification } from "antd";
import { useState } from "react";
import { PrimaryButton } from "../../styled/Button.styled";
import { useUploadVehicleExcelMutation } from "../../../feature/Category/categoryApiSlice";
import { useGetAllVehicleMakeQuery } from "../../../feature/VehicleList/vehicleListApiSlice";
import { IVehicleMake } from "../../../types/vehicle";
import * as XLSX from "xlsx";

type VehicleExcelUploadProps = {
  onClose: () => void;
};

function VehicleExcelUpload(props: VehicleExcelUploadProps) {
  const { onClose } = props;
  const [file, setFile] = useState<File | null>(null);
  const [uploadVehicleExcel] = useUploadVehicleExcelMutation();
  const { data: vehicleList, isFetching: isLoadingVehicles } = useGetAllVehicleMakeQuery();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Check file extension instead of MIME type for better compatibility
    const fileName = selectedFile.name.toLowerCase();
    const validExtensions = ['.xlsx', '.xls'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      notification.error({
        message: "Please upload Excel file only (.xlsx or .xls)",
        placement: "bottomLeft",
      });
      return;
    }

    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      notification.error({
        message: "Please select a file to upload",
        placement: "bottomLeft",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const result: any = await uploadVehicleExcel(formData).unwrap();
      // The server answers 200 even when it refused the file, with the reasons in the body.
      let body: any = result;
      if (typeof result === "string") {
        try {
          body = JSON.parse(result);
        } catch {
          body = {};
        }
      }
      if (body?.status === "FAILED") {
        const reasons = Object.values(body.fieldErrorMessageMap ?? {}).join(", ");
        notification.error({
          message: reasons || "The Excel was not saved. Please check the file.",
          placement: "bottomLeft",
        });
        return;
      }
      notification.success({
        message: "Vehicle Excel uploaded successfully",
        placement: "bottomLeft",
      });
      onClose();
    } catch (error) {
      notification.error({
        message: "Error uploading vehicle Excel",
        placement: "bottomLeft",
      });
    }
  };

  // The upload skips the first two rows (a title row and the column headings) and reads the makes from the third
  // row on, so every file made here has both, or its first data row would be ignored when it is uploaded.
  const sheetRows = (dataRows: string[][]) => [
    ["Vehicle Data"],
    ["Vehicle Make", "Vehicle Model", "ID"],
    ...dataRows,
  ];

  const downloadSheet = (rows: string[][], fileName: string) => {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 28 }, { wch: 90 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicle Data");
    XLSX.writeFile(wb, fileName);
  };

  // Every vehicle make and its models as they are saved now, in the layout the upload reads (one row per make,
  // models separated by commas). This one file is both the list to look at and the one to add to: add or change
  // rows in it and upload it back. When there are no vehicles yet it is just the title and the headings.
  const handleDownloadExisting = () => {
    if (!vehicleList) {
      notification.error({
        message: "The vehicle list is still loading. Please try again in a moment.",
        placement: "bottomLeft",
      });
      return;
    }
    const makes: IVehicleMake[] = vehicleList.data ?? [];
    const clean = (value: string) => value.replace(/\s+/g, " ").trim();
    // The ID column is how the upload knows which saved make a row is: with it, a changed spelling or case is saved
    // on that make, a model taken out of the cell is removed, and a row deleted from the file removes the make.
    // A make with no models is kept in the file (blank models), or uploading the file would remove it.
    const rows = makes
      .map((make) => [
        clean(make.vehicleMake ?? ""),
        (make.vehicleModel ?? [])
          .map(clean)
          .filter((model) => model.length > 0)
          .join(", "),
        String(make.id),
      ])
      .filter(([name]) => name.length > 0);
    downloadSheet(
      sheetRows(rows),
      `vehicles_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <h3>Upload Vehicle Excel</h3>
        </Col>
        <Col span={24}>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
            }}
          />
        </Col>
        {file && (
          <Col span={24}>
            <p>Selected file: {file.name}</p>
          </Col>
        )}
        <Col span={24}>
          <Button
            type="link"
            onClick={handleDownloadExisting}
            loading={isLoadingVehicles}
            style={{ padding: 0 }}
          >
            Download Vehicles Excel
          </Button>
          <p style={{ margin: "2px 0 0", color: "#8c8c8c", fontSize: "10px", lineHeight: "1.3" }}>
            Edit the file and upload it: changed names and models are saved, and rows or models you delete are removed. To add, put a new row (make, models) with the ID column empty.
          </p>
        </Col>
        <Col span={24} style={{ marginTop: "20px", textAlign: "right" }}>
          <Button 
            onClick={onClose} 
            style={{ marginRight: "10px" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpload}
            disabled={!file}
          >
            Upload
          </Button>
        </Col>
      </Row>
    </div>
  );
}

export default VehicleExcelUpload;